// Product brief for Life Insurance with an LTC acceleration rider. One
// strategy serves the generic `lifeInsuranceWithRider`, Transamerica FFIUL
// (`lifeWithIndexAccount`), and Nationwide No-Lapse Guarantee UL II
// (`noLapseGuaranteeLife`). See cmTwo.ts for the format intent.

export const lifeWithRiderBrief = `**Life Insurance with LTC Rider** — a single death-benefit pool accelerated
to pay for long-term care (calculationType \`lifeInsuranceWithRider\`,
single-life only). One strategy serves three products: the generic
\`lifeInsuranceWithRider\`, Transamerica Financial Foundation IUL
(\`lifeWithIndexAccount\`), and Nationwide No-Lapse Guarantee UL II
(\`noLapseGuaranteeLife\`).

Pool model — **single accelerated pool (the death benefit IS the pool)**:
- \`policyMaximumBenefitAmount\` is the Specified Amount / face — both the single
  accelerable LTC pool AND the death benefit. It seeds the \`base_pool\` (less
  prior utilization). There is **no continuation pool** — \`eob\` is $0 on every
  row. This matches the legacy single-pool routing
  (\`isHybridPolicy(lifeInsuranceWithRider) === false\` → single-pool
  \`calculateStandardPolicyBenefit\`).
- The \`db\` column carries the **residual death benefit**. LTC acceleration
  reduces it dollar-for-dollar, floored at the **Residual Death Benefit**
  (\`policyMinimumGuaranteedBenefitAmount\` — e.g. Nationwide's 10% of Specified
  Amount = $40,000 on a $400,000 face). The DB opens NET OF prior utilization
  (the pool IS the DB).

Inflation — **split rates, simple or compound**:
- These products are NOT in \`hasFlatDeathBenefit\`, so the DB inflates.
- The pool / monthly cap inflate on \`policyInflationProtection\`; the DB
  inflates on \`policyInflationProtectionOnFaceAmount ?? policyInflationProtection\`.
  These coincide unless the face-amount knob is set separately, where they
  diverge at rate > 0.
- \`isSimpleInflationProtection\` switches simple (linear) vs compound growth.
- The sample NLG UL II illustration elects 0% inflation, so DB and pool are
  flat $400,000 every year (split-rate divergence is invisible at 0%).

Payout model — **reimbursement (default) or cash indemnity**:
- **Reimbursement** (default, generic life-with-rider): pays
  min(eligible cost, pro-rated monthly cap) from the single pool.
- **Indemnity** (NLG UL II — "Cash Indemnity Benefits ... no bills or receipts
  are needed", illustration p.8): pays the full pro-rated monthly cap as cash
  regardless of actual cost, drawn from the pool. Cash above the eligible cost
  accrues as an \`indemnitySurplus\` reserve, drained on a later shortfall month.

Elimination period:
- Standard two-tier (home/facility) EP. EP days are out-of-pocket
  (\`in-wp\` rows). No retroactive lump (not a retroactive-EP product).

Ground truth — sample illustration (\`Nationwide No-Lapse Guarantee UL II
F55 (1).pdf\`, Female 55, CA, Preferred Nontobacco):
- **$400,000 Specified Amount** = LTC Rider Amount = the single pool AND the
  death benefit; "Benefit for LTC" and "Net Death Benefit" are flat $400,000
  every year (no inflation elected).
- **Monthly cap $8,000** = elected 2% × $400,000 (the binding leg; the 2×HIPAA
  leg is $24,600 and never binds). Daily ≈ $266.67. At max draw the pool lasts
  ≈ 50 months (~4.2 years).
- **Cash indemnity** — pays the $8,000 cap regardless of billed cost.
- **Residual DB floor $40,000** = 10% of the LTC Specified Amount;
  beneficiaries get at least this even after full acceleration.
- **90-day elimination period**, no retroactive lump. Premium is a No-Lapse
  solve paid once ($122,262 in year 1, $0 after).

Things to eyeball:
- \`eob\` is $0 on every row — single pool.
- DB reduction equals the pool drawdown until the floor clamps it; on the
  flooring row \`flooredAtMinimum\` is true and the reduction is capped.
- For an indemnity policy, the payout can exceed the actual care cost (the
  excess builds the surplus reserve); for reimbursement it never does.
- Bit-equal parity holds only at 0% inflation (legacy inflates the pool
  end-of-phase-anchored; this strategy inflates per anniversary).
`;
